import React, { useState } from 'react';
import { generateFAQs, assessSituation, FAQResponse, FieldContextOverrides } from '../../../utils/faqApi';
import { FullFieldContext } from './FieldEditor';

type TestTab = 'faqs' | 'assess';

interface LiveTestPanelProps {
  topic: string;
  canonical: FullFieldContext | null;
  edited: FullFieldContext | null;
  debugConfig?: boolean;
}

// Compute only changed fields to send as overrides
function computeDeltaOverrides(
  canonical: FullFieldContext,
  edited: FullFieldContext
): FieldContextOverrides | undefined {
  const delta: FieldContextOverrides = {};
  (Object.keys(edited) as Array<keyof FullFieldContext>).forEach((key) => {
    if (JSON.stringify(canonical[key]) !== JSON.stringify(edited[key])) {
      (delta as any)[key] = edited[key];
    }
  });
  return Object.keys(delta).length > 0 ? delta : undefined;
}

const LiveTestPanel: React.FC<LiveTestPanelProps> = ({ topic, canonical, edited, debugConfig = false }) => {
  const [tab, setTab] = useState<TestTab>('faqs');
  const [assessInput, setAssessInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<FAQResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showResolvedContext, setShowResolvedContext] = useState(false);

  const deltaOverrides =
    canonical && edited ? computeDeltaOverrides(canonical, edited) : undefined;
  const hasChanges = !!deltaOverrides;

  const handleRun = async () => {
    setLoading(true);
    setResult(null);
    setError(null);
    try {
      let response: FAQResponse;
      if (tab === 'faqs') {
        response = await generateFAQs(topic, 2, undefined, deltaOverrides, debugConfig);
      } else {
        if (!assessInput.trim()) {
          setError('Please enter a situation to assess.');
          setLoading(false);
          return;
        }
        response = await assessSituation(assessInput, topic, undefined, deltaOverrides, debugConfig);
      }
      setResult(response);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-4">
        {(['faqs', 'assess'] as TestTab[]).map((t) => (
          <button
            key={t}
            onClick={() => { setTab(t); setResult(null); setError(null); }}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t
                ? 'border-gov-blue text-gov-blue'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {t === 'faqs' ? 'Generate FAQs' : 'Assess Situation'}
          </button>
        ))}
      </div>

      {/* Override indicator */}
      {hasChanges ? (
        <div className="mb-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
          Sending {Object.keys(deltaOverrides!).length} override field{Object.keys(deltaOverrides!).length !== 1 ? 's' : ''}:{' '}
          <span className="font-mono">{Object.keys(deltaOverrides!).join(', ')}</span>
        </div>
      ) : (
        <div className="mb-3 text-xs text-gray-400 italic">
          No overrides — will use canonical Lambda config.
        </div>
      )}

      {/* Assess input */}
      {tab === 'assess' && (
        <textarea
          rows={3}
          value={assessInput}
          onChange={(e) => setAssessInput(e.target.value)}
          placeholder="Describe a situation to assess against this topic's reporting requirements…"
          className="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none mb-3"
        />
      )}

      {/* Run button */}
      <button
        onClick={handleRun}
        disabled={loading || !topic}
        className="w-full px-4 py-2 bg-gov-blue text-white rounded hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium mb-4"
      >
        {loading ? 'Running…' : 'Run Test'}
      </button>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700 mb-3">
          {error}
        </div>
      )}

      {/* Results */}
      {result && (
        <div className="space-y-3">
          {/* FAQs */}
          {result.guidance?.faqs && result.guidance.faqs.map((faq, i) => (
            <div key={i} className="p-3 bg-blue-50 border border-blue-100 rounded">
              <p className="text-sm font-semibold text-gray-900 mb-1">{faq.question}</p>
              <p className="text-sm text-gray-700 leading-relaxed">{faq.answer}</p>
            </div>
          ))}

          {/* Assessment */}
          {result.guidance?.assessment && (() => {
            const a = result.guidance!.assessment!;
            return (
              <div className="p-3 bg-blue-50 border border-blue-100 rounded space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-gray-600 uppercase">Determination</span>
                  <span className="text-sm font-medium text-gray-900">{a.determination}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-gray-600 uppercase">Confidence</span>
                  <span className="text-sm text-gray-700">{Math.round((a.confidence || 0) * 100)}%</span>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">{a.reasoning}</p>
                {a.recommendation && (
                  <p className="text-sm text-blue-800 font-medium">{a.recommendation}</p>
                )}
              </div>
            );
          })()}

          {/* Resolved field context (collapsible) */}
          {result.field_context && (
            <div className="border border-gray-200 rounded overflow-hidden">
              <button
                onClick={() => setShowResolvedContext(!showResolvedContext)}
                className="w-full flex items-center justify-between px-3 py-2 text-xs font-medium text-gray-600 bg-gray-50 hover:bg-gray-100"
              >
                <span>Resolved field_context (what Lambda used)</span>
                <span>{showResolvedContext ? '▲' : '▼'}</span>
              </button>
              {showResolvedContext && (
                <pre className="bg-gray-900 text-green-400 font-mono text-xs p-3 overflow-x-auto whitespace-pre-wrap">
                  {JSON.stringify(result.field_context, null, 2)}
                </pre>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LiveTestPanel;
